import React, { useState } from "react";
import {
  outerStyle,
  loginCardStyle,
  headerSectionStyle,
  titleStyle,
  redSpanStyle,
  subtitleStyle,
  formSectionStyle,
  fieldGroupStyle,
  fieldLabelStyle,
  inputStyle,
  errorMsgStyle,
  loginButtonStyle,
  helpLinkStyle,
} from "../styles/LoginStyles";
import { useNavigate } from 'react-router-dom';
import BannerBar from "./BannerBar";
import CopyRightBanner from "./CopyrightBanner";

const getRootDomain = (): string | null => {
  try {
    const host = window.location.hostname;
    const parts = host.split('.').filter(Boolean);
    if (parts.length < 1) return null;
    const root = parts.slice(-2).join('.');
    return `.${root}`;
  } catch {
    return null;
  }
};

const encodeCookieValue = (name: string, value: string): string => {
  if (name === 'email') {
    return value;
  }
  return encodeURIComponent(value);
};

const setCookie = (name: string, value: string, maxAgeSeconds: number) => {
  const rootDomain = getRootDomain();
  const encoded = encodeCookieValue(name, value);
  const cookieValue = `${name}=${encoded}; Path=/; Max-Age=${maxAgeSeconds}; domain=${rootDomain}`;
  document.cookie = cookieValue;
};

const LoginModal: React.FC = () => {
  const [dailyPin, setDailyPin] = useState('');
  const [storeNumber, setStoreNumber] = useState<string>('');
  const [dailyPinFromServer, setDailyPinFromServer] = useState<string | null>(null);
  const [isFetchingDailyPin, setIsFetchingDailyPin] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isStoreNumberLocked, setIsStoreNumberLocked] = useState(false);
  const [, setUser] = useState<string>(sessionStorage.getItem('userName') || '');
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const fetchDailyPin = async () => {
    if (!storeNumber) {
      setError('Store Number is required.');
      return false;
    }

    try {
      setError('');
      setIsFetchingDailyPin(true);
      const url = `https://mystore.edible.tech/api/auth/rotilogin/store/daily-pin`;
      let response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ storeNumber }),
      });

      if (!response.ok) {
        throw new Error('Unable to retrieve the Daily PIN. Please verify the store number.');
      }

      const data = await response.json();
      const pinFromApi = data?.dailyPin ?? data?.daily_pin ?? data?.pin;

      if (!pinFromApi) {
        throw new Error('Daily PIN is not available for this store.');
      }

      setDailyPinFromServer(pinFromApi);
      setDailyPin('');
      setIsStoreNumberLocked(true);
      return true;
    } catch (err) {
      setDailyPinFromServer(null);
      setIsStoreNumberLocked(false);
      const message = err instanceof Error ? err.message : 'Failed to fetch Daily PIN. Please try again.';
      setError(message);
      return false;
    } finally {
      setIsFetchingDailyPin(false);
    }
  };

  const handleLogin = async () => {
    if (!storeNumber) {
      setError('Store Number is required.');
      return;
    }

    if (!isStoreNumberLocked) {
      await fetchDailyPin();
      return;
    }

    if (!dailyPin) {
      setError('Daily PIN is required to login.');
      return;
    }

    try {
      setError('');
      setIsLoggingIn(true);
      const url = `https://mystore.edible.tech/api/auth/rotilogin/store`;
      let response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ storeNumber, dailyPin }),
      });

      if (response && response.ok) {
        const data = await response.json();

        const { authToken, userName, refreshToken, email } = data;

        sessionStorage.setItem('authToken', authToken);
        sessionStorage.setItem('refreshToken', refreshToken);
        sessionStorage.setItem('userName', userName);
        sessionStorage.setItem('storeNumber', storeNumber);
        sessionStorage.setItem('email', email || '');
        localStorage.setItem('authToken', authToken);
        localStorage.setItem('refreshToken', refreshToken);
        localStorage.setItem('userName', userName);
        localStorage.setItem('storeNumber', storeNumber);
        localStorage.setItem('email', email || '');
        setCookie('authToken', authToken, 43200);
        setCookie('refreshToken', refreshToken, 43200);
        setCookie('userName', userName, 43200);
        setCookie('storeNumber', storeNumber, 43200);
        setCookie('email', email || '', 43200);
        setUser(userName);
        setError('');
        navigate('/');
      } else {
        setError('Invalid credentials. Please try again.');
      }
    } catch (error) {
      setError('An error occurred during login. Please try again later.');
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleLogin();
    }
  };

  return (
    <div style={outerStyle}>
      <BannerBar user={''} />
      <div style={loginCardStyle}>
        <div style={headerSectionStyle}>
          <h1 style={titleStyle}>
            Login to <span style={redSpanStyle}>My Roti</span>
          </h1>
          <div style={subtitleStyle}>
            Enter your Store Number to retrieve today's PIN suggestion
          </div>
        </div>
        <div style={formSectionStyle}>
          <div style={fieldGroupStyle}>
            <label htmlFor="storeNumber" style={fieldLabelStyle}>
              Store number*
            </label>
            <input
              id="storeNumber"
              style={inputStyle}
              placeholder="e.g. 101"
              value={storeNumber}
              onChange={e => {
                if (isStoreNumberLocked) return;
                setStoreNumber(e.target.value);
                setDailyPin('');
                setDailyPinFromServer(null);
                setError('');
              }}
              onKeyDown={handleKeyDown}
              autoComplete="off"
              disabled={isStoreNumberLocked || isFetchingDailyPin || isLoggingIn}
            />
          </div>
          {isStoreNumberLocked && (
            <>
              <div style={fieldGroupStyle}>
                <label htmlFor="dailyPin" style={fieldLabelStyle}>
                  Daily pin*
                </label>
                <input
                  id="dailyPin"
                  type="text"
                  style={inputStyle}
                  placeholder="Enter your PIN"
                  value={dailyPin}
                  onChange={e => setDailyPin(e.target.value)}
                  onKeyDown={handleKeyDown}
                  autoComplete="off"
                  disabled={isLoggingIn}
                />
              </div>
              {dailyPinFromServer && (
                <div
                  style={{
                    ...fieldLabelStyle,
                    fontSize: '14px',
                    marginTop: '-8px',
                    marginBottom: '12px',
                  }}
                >
                  Today's PIN suggestion: <span style={{ fontWeight: 700 }}>{dailyPinFromServer}</span>
                </div>
              )}
            </>
          )}
          {error && <div style={errorMsgStyle}>{error}</div>}
          <button
            style={loginButtonStyle}
            onClick={handleLogin}
            disabled={isFetchingDailyPin || isLoggingIn}
          >
            {isStoreNumberLocked
              ? (isLoggingIn ? 'Logging in...' : 'Login')
              : (isFetchingDailyPin ? 'Fetching PIN Suggestion...' : 'Fetch PIN Suggestion')}
          </button>
        </div>
        <div style={helpLinkStyle}>
          Need help? Tap{" "}
          <a
            href="mailto:support@netsolace.com"
            style={{ color: "#31587C", textDecoration: "underline" }}
          >
            here to contact <span style={{ textTransform: "capitalize" }}>Netsolace</span>
          </a>
        </div>
      </div>
            <CopyRightBanner />
    
    </div>
  );
};


export default LoginModal;
